/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import {inject} from 'mobx-react';
import DocumentTitle from 'react-document-title';
import {isClassComponent, toClass} from 'recompose';

import {Newable} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {StoreProps, translate} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
type AnyStatelessComponent<P = any> = React.StatelessComponent<P>;
type ReactComponentFactory<P = any> = Newable<React.Component<P, any>>;
type ConstructableComponent<P = any> =
    | ReactComponentFactory<P>
    | Newable<any>
    | AnyStatelessComponent<P>;

type ReturnComponent = ReactComponentFactory<StoreProps>;

/******************************************** HELPERS *********************************************/
const buildDisplayName = (CleanWrappedComponent: ReturnComponent) => {
    const displayName =
        CleanWrappedComponent.displayName || CleanWrappedComponent.name || `Unknown`;
    return `withDocumentTitle(${displayName})`;
};

/********************************************* EXPORT *********************************************/
/**
 **************************************************************************************************
 * NOTE: CAN CAUSE INPUTS TO ONLY ACCEPT 1 CHAR AT A TIME, DESELECTING AFTER A SINGLE KEYPRESS    *
 *     *   Reason: a whole new component gets created every time                                  *
 *     *   This is very rare, but keep an eye out for it when decorating a component with this    *
 **************************************************************************************************
 *
 * Set the document title immediately on loading the component
 * All titles set here are followed by ` - CANImmunize`
 * Translates titles before display
 *
 * Example: @withDocumentTitle(`Signup - Location`) class MyClass { ... }
 *
 * Example:
 *     const enhance = compose(
 *         inject('appState'),
 *         withDocumentTitle(`country`)
 *     ) as any;
 *     export const someStatelessFunction = enhance((props) => <div> Hello </div>);
 *
 * @param {string} titleID String ID of string to display in doc title, before
 *                         ` - CANImmunize`
 * @param {boolean} usesName If true, use record's 1st & last name in the title
 */
export function withDocumentTitle({titleID, usesName}: {titleID: string; usesName?: boolean}) {
    return function logOnRenderHOC<T extends ConstructableComponent>(WrappedComponent: T): T {
        const CleanWrappedComponent = !isClassComponent(WrappedComponent)
            ? (toClass(WrappedComponent) as ReactComponentFactory<StoreProps>)
            : (WrappedComponent as ReactComponentFactory<StoreProps>);

        /**
         * Returned class
         */
        @inject('appState')
        class ComponentWithDocTitle extends CleanWrappedComponent {
            state = this.state || {};
            events = this.events || {};

            render() {
                const {appState} = this.props;

                // Get name associated with current record if present & requested by config
                let name = ``;
                if (usesName && appState.activeRecordModel) {
                    name = ` (${appState.activeRecordModel.fullName.trim()})`;
                }

                return (
                    <DocumentTitle title={`${translate(titleID)}${name} - CANImmunize`}>
                        {super.render()}
                    </DocumentTitle>
                );
            }
        }

        ComponentWithDocTitle.displayName = buildDisplayName(CleanWrappedComponent);

        return ComponentWithDocTitle as T;
    };
}
